import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Briefcase, Check, CheckCircle2, Clock, IndianRupee, Loader2, Radar, XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import AgentAvatar from "@/components/AgentAvatar";

const fmtINR = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

// Client-facing status labels (internal blockers stay invisible to the client).
const CLIENT_STATUS = {
  sent: { label: "Proposal received", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  in_progress: { label: "In progress", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  action_needed: { label: "In progress", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  delivered: { label: "Delivered", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function BriefTrackPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const canceled = searchParams.get("payment") === "canceled";

  const [brief, setBrief] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [payState, setPayState] = useState(sessionId ? "checking" : null); // checking | paid | timeout
  const [paidProposalId, setPaidProposalId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [b, p] = await Promise.all([
        api.get(`/briefs/${id}`),
        api.get(`/briefs/${id}/proposals`),
      ]);
      setBrief(b.data);
      setProposals(p.data.proposals || []);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  // Poll payment status after returning from Stripe.
  const attemptsRef = useRef(0);
  useEffect(() => {
    if (!sessionId) return;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setPayState("paid");
          if (r.data.proposal_id) setPaidProposalId(r.data.proposal_id);
          load();
          return;
        }
      } catch { /* transient */ }
      if (attemptsRef.current >= 12) {
        setPayState("timeout");
        return;
      }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, load]);

  if (notFound) {
    return (
      <div data-testid="brief-not-found" className="mx-auto max-w-3xl px-6 py-24 text-center">
        <p className="font-display text-3xl font-black text-slate-900">Brief not found</p>
        <p className="mt-2 text-slate-500">This link may be wrong or the brief was removed.</p>
        <Link to="/marketplace" className="mt-6 inline-flex rounded-md bg-blue-600 text-white text-sm font-bold px-5 py-3 hover:bg-blue-700">
          Browse the marketplace
        </Link>
      </div>
    );
  }

  if (!brief) {
    return (
      <div data-testid="brief-track-loading" className="mx-auto max-w-3xl px-6 py-24 flex items-center justify-center gap-3 text-slate-500">
        <Loader2 className="animate-spin" size={20} /> Loading brief…
      </div>
    );
  }

  return (
    <div data-testid="brief-track-page" className="mx-auto max-w-4xl px-5 md:px-8 pt-10 pb-24">
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
        <Link to="/marketplace" className="hover:text-blue-600">Marketplace</Link>
        <span>/</span>
        <span className="text-slate-900 truncate">Your brief</span>
      </div>

      {/* Payment banners */}
      {payState === "checking" && (
        <div data-testid="payment-checking-banner" className="mb-6 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-center gap-2.5 text-sm text-blue-800">
          <Loader2 size={16} className="animate-spin" /> Confirming your payment with Stripe…
        </div>
      )}
      {payState === "paid" && (
        <div data-testid="payment-success-banner" className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2.5 text-sm text-emerald-800">
          <CheckCircle2 size={16} strokeWidth={2.4} /> Payment confirmed — the agent has started working. Track live updates below.
        </div>
      )}
      {payState === "timeout" && (
        <div data-testid="payment-timeout-banner" className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          We couldn't confirm the payment yet. If you completed checkout, this page will update automatically in a minute.
        </div>
      )}
      {canceled && !payState && (
        <div data-testid="payment-canceled-banner" className="mb-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-2.5 text-sm text-slate-700">
          <XCircle size={16} /> Checkout canceled — your brief is untouched. You can accept a proposal anytime.
        </div>
      )}

      {/* BRIEF HEADER */}
      <header data-testid="brief-header" className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50/80 via-white to-blue-50/40 p-6 md:p-8">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">
            <Briefcase size={10} strokeWidth={2.5} /> {brief.category}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
            <Clock size={11} /> {brief.timeline_days} days
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
            <IndianRupee size={11} strokeWidth={2.5} /> {fmtINR(brief.budget_min)}–{fmtINR(brief.budget_max)}
          </span>
        </div>
        <h1 className="mt-3 font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-slate-900 leading-tight">
          {brief.title}
        </h1>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-2xl">{brief.description}</p>
        <p className="mt-3 text-xs text-slate-400">
          Posted by {brief.client_name}{brief.client_company ? ` · ${brief.client_company}` : ""}
        </p>
      </header>

      {/* PROPOSALS */}
      <section className="mt-10">
        <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-slate-900">
          Proposals from agents
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Every proposal was reviewed and approved by the human freelancer behind the agent before reaching you.
        </p>

        {proposals.length === 0 ? (
          <div data-testid="proposals-empty" className="mt-6 rounded-2xl border border-dashed border-slate-300 px-6 py-10 text-center">
            <Radar size={26} className="mx-auto text-blue-500 animate-pulse" />
            <p className="mt-3 font-display font-bold text-slate-900">Agents are drafting proposals…</p>
            <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
              Matching agents have picked up your brief. Their freelancers approve each proposal before it lands here — check back shortly or keep this page open.
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            {proposals.map((p) => (
              <ClientProposalCard
                key={p.id}
                proposal={p}
                justPaid={paidProposalId === p.id}
                payDisabled={payState === "checking"}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ClientProposalCard({ proposal: p, justPaid = false, payDisabled = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Once paid, treat the proposal as in-progress immediately — the backend flips
  // status a moment later, but the pay button must disappear right away.
  const effectiveStatus = justPaid && p.status === "sent" ? "in_progress" : p.status;
  const meta = CLIENT_STATUS[effectiveStatus] || CLIENT_STATUS.sent;
  const events = (p.events || []).filter((e) => e.type !== "flag");

  const pay = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(`/proposals/${p.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout. Try again.");
      setBusy(false);
    }
  };

  return (
    <article data-testid={`proposal-card-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-[0_4px_20px_-14px_rgba(15,23,42,0.25)]">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3.5 min-w-0">
          <AgentAvatar seed={p.agent_avatar_seed || p.agent_name} size={52} />
          <div className="min-w-0">
            <p className="font-display text-lg font-black tracking-tight text-slate-900 leading-tight">{p.agent_name}</p>
            <p className="text-xs text-slate-500">{p.agent_role} · autonomous AI agent</p>
            <span className={`mt-1.5 inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${meta.cls}`}>
              {meta.label}
            </span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Quote</p>
          <p className="font-display text-2xl font-black tracking-tight text-slate-900">{fmtINR(p.amount_inr)}</p>
          <p className="text-[11px] text-slate-500">{p.timeline_days} days</p>
        </div>
      </div>

      <p className="mt-4 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{p.cover_letter}</p>

      {p.milestones?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {p.milestones.map((m, i) => (
            <span key={i} className="text-[11px] px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 font-medium">
              {i + 1}. {m}
            </span>
          ))}
        </div>
      )}

      {/* Delivered package */}
      {p.deliverables && (
        <div data-testid={`client-deliverables-${p.id}`} className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-3.5">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">Your delivery</p>
          <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{p.deliverables.note}</p>
          <ul className="mt-2 space-y-1">
            {p.deliverables.items?.map((it, i) => (
              <li key={i} className="text-xs text-emerald-800 font-mono flex items-start gap-1.5">
                <Check size={12} className="mt-0.5 shrink-0" strokeWidth={2.6} /> {it}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Live status updates */}
      {effectiveStatus !== "sent" && events.length > 0 && (
        <ol data-testid={`client-timeline-${p.id}`} className="mt-4 relative border-l border-slate-200 ml-1 space-y-3.5">
          {events.slice().reverse().map((e) => (
            <li key={e.id} className="relative pl-5">
              <span className={`absolute -left-[4.5px] top-1.5 w-2 h-2 rounded-full ring-4 ring-white ${
                e.type === "payment" ? "bg-emerald-500" : e.type === "delivery" ? "bg-emerald-600" : "bg-blue-500"
              }`} />
              <p className="text-sm text-slate-700 leading-snug">{e.text}</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 mt-0.5">
                {new Date(e.ts).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </p>
            </li>
          ))}
        </ol>
      )}

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      {effectiveStatus === "sent" && (
        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] text-slate-400">
            Secure payment via Stripe · funds kick off the project instantly
          </p>
          <button
            type="button"
            onClick={pay}
            disabled={busy || payDisabled}
            data-testid={`pay-btn-${p.id}`}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 text-white text-sm font-bold px-5 py-3 hover:bg-blue-700 disabled:opacity-60 transition shadow-[0_18px_38px_-18px_rgba(37,99,235,0.7)]"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <IndianRupee size={15} strokeWidth={2.6} />}
            Accept & pay {fmtINR(p.amount_inr)}
          </button>
        </div>
      )}
    </article>
  );
}
